import PCLNAnalytics from '@pcln/analytics'
import random from 'lodash/random'
import { isServerSide } from '../isServerSide'

function fireGtm(type, eventName, category, action, label, value, callback) {
  if (!isServerSide) {
    // Log event to Splunk (this used to be handled by np-pcln-gtm)
    const tag = {
      action,
      category,
    }

    if (label) {
      tag.label = label
    }

    if (value) {
      // PCLNAnalytics ignores everything that isn't a string, so we need to stringify value
      tag.value = JSON.stringify(value)
    }

    PCLNAnalytics.log(tag)

    const event = {
      event: eventName,
      'gtm-event-action': action,
      'gtm-event-category': category,
      'gtm-event-label': label,
      'gtm-event-value': value,
      eventCallback: callback,
      eventTimeout: 1000,
    }

    // As this function is called repeatedly, this has the effect of staggering the execution
    // of Google Tag Manager updates (updating dataLayer, not to be confused with using PCLNAnalytics.logEvent)
    const delay = random(0, 500)
    requestIdleCallback(() => setTimeout(() => window?.dataLayer?.push(event), delay), {
      timeout: 15000, // 15 seconds
    })
  }
}

function splitGetLabel(isFused, isInterline) {
  let result

  if (isFused) {
    result = 'Split'
  } else {
    result = 'Single'
  }
  if (isInterline) {
    result += ' interline'
  }

  return result
}

const logicFunctions = {
  fireGtm,
  timers: {},
  retailListingPosition: 0,

  startTimer(key) {
    if (isServerSide) {
      return
    }
    const timeStart =
      window.performance && window.performance.now ? window.performance.now() : new Date().getTime()
    this.timers[key] = { timeStart }
  },

  endTimer(key) {
    if (isServerSide) {
      return
    }
    const timeEnd =
      window.performance && window.performance.now ? window.performance.now() : new Date().getTime()
    this.timers[key].timeEnd = timeEnd
  },

  retrieveTimer(key) {
    const timer = this.timers[key]
    let retrievedTimes = false
    if (timer && timer.timeStart && timer.timeEnd) {
      const millisTime = timer.timeEnd - timer.timeStart
      const secondsTime = millisTime / 1000
      delete this.timers[key]
      retrievedTimes = { millisTime, secondsTime }
    }
    return retrievedTimes
  },

  fire: {
    clickedUpdateSearchMobile() {
      fireGtm('send', 'event', 'MFLY-Listings', 'No flights found', 'Change search clicked')
    },

    airSearchCallInitiated() {
      fireGtm('send', 'event', 'MFLY-Listings', 'Search Call Initiated')
    },

    viewMoreFlights(pageNumber) {
      fireGtm('send', 'event', 'MFLY-Listings', 'View More Flights', pageNumber)
    },

    responseTime(timeseconds, category) {
      const time = Math.round(timeseconds)
      let label
      switch (true) {
        case time === 0:
          label = '<1'
          break
        case time === 1:
          label = '1'
          break
        case time === 2:
          label = '2'
          break
        case time === 3:
          label = '3'
          break
        case time === 4:
          label = '4'
          break
        case time >= 5 && time <= 6:
          label = '5-6'
          break
        case time >= 7 && time <= 8:
          label = '7-8'
          break
        case time >= 9 && time <= 10:
          label = '9-10'
          break
        case time >= 11 && time <= 15:
          label = '11-15'
          break
        case time >= 16 && time <= 20:
          label = '16-20'
          break
        case time >= 21:
          label = '21+'
          break
        default:
          label = 'unknown'
      }
      if (category === 'Rental Car Add on') {
        fireGtm('send', 'event', 'MFLY-Listings', category, label)
      } else {
        fireGtm('send', 'event', 'MFLY-Listings', 'Search result time', label)
      }
    },

    airSearchResponse(label, tripType) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Search result', label, tripType)
    },

    filteredResponse(label, tripType) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Filtered Search result', label, tripType)
    },

    initialListingsRendering(label) {
      fireGtm('send', 'event', 'MFLY-Listings', 'initial listings rendered', label)
    },

    // AIR-22222 / AIR-22631
    numOfItinerariesReturned(num) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Itineraries Returned', 'Number of Itineraries Returned', num)
    },

    retailListingsPositionClicked(position) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Itinerary Selected - RTL', `Listing Position ${position}`)
    },

    sopqAction(position, action) {
      fireGtm('send', 'gtm-custom-event', 'MFLY-Listings', `sopq_${action}`, position)
    },

    sopqUrl(url, label) {
      fireGtm('send', 'gtm-custom-event', 'MFLY-Listings', `${url}`, label)
    },

    splitLowestPriceFullResult: (itineraryTypeCode, isFused, isInterline) => {
      if (itineraryTypeCode !== 'OW') {
        fireGtm('send', 'event', 'MFLY-Listings', 'Lowest Price Search', splitGetLabel(isFused, isInterline))
      }
    },

    nearbyAirportResult: () => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Nearby Airports Result', 'Incomplete Search Request')
    },

    popularFlights: (action) => {
      fireGtm('send', 'event', 'MFLY-Listings', action, 'Popular')
    },

    listingsRenderTime: (value) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Web Listings Render Time', value)
    },

    cugEligible: (value) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Merchandising', value)
    },

    priceConfirmation(message) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Price Confirm Result', message)
    },

    toExpandCheckoutBarDetail() {
      fireGtm('send', 'event', 'MFLY-Details', 'expand_summaryofcharges')
    },

    filterRedirect(error, requestType) {
      fireGtm('send', 'event', 'MFLY-Listings', `${requestType} redirect error`, null, error)
    },

    dealsTutorialClicked: () => {
      fireGtm('send', 'event', 'Deals-Details', 'Deals Intro Copy', 'Tutorial Open')
    },

    alternateExdClicked: (position, numOfAltDeals) => {
      fireGtm('send', 'event', 'Deals-Details', 'click', `Selected deal ${position} of ${numOfAltDeals}`)
    },

    expressDealSelected: (cheapestExd, selectedExd) => {
      let label
      let value
      if (selectedExd.priceKey) {
        label =
          selectedExd.priceKey === cheapestExd.priceKey ? 'Cheapest EXD selected' : 'Alternate EXD selected'
        value = `Cheapest deal priceKey: ${cheapestExd.priceKey}. Selected deal priceKey: ${selectedExd.priceKey}`
      } else {
        const cheapestExdItineraryReference = cheapestExd?.itineraryReference
          ? cheapestExd?.itineraryReference
          : cheapestExd?.itineraryReferenceInfo
        const selectedExdItineraryReference = selectedExd?.itineraryReference
          ? selectedExd?.itineraryReference
          : selectedExd?.itineraryReferenceInfo
        label =
          cheapestExdItineraryReference.refId === selectedExdItineraryReference.refId
            ? 'Cheapest EXD selected'
            : 'Alternate EXD selected'
        value = `Cheapest deal refId: ${cheapestExdItineraryReference}. Selected deal refId: ${selectedExdItineraryReference.refId}`
      }

      fireGtm('send', 'event', 'Deals-Details', 'click', label, value)
    },
  },
}

export { logicFunctions as flyGaLogic, fireGtm }
